import { Injectable } from '@angular/core';

// RxJs
import { Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';

// Services
import { GraphQLService } from '@core/providers/services/graphql/graphql.service';

// Models
import { User, UserRole, UserStatus } from '@core/models/maintainer.interface';



@Injectable({
  providedIn: 'root'
})
export class UsersService {

  private options: { countries: any, centers: any, roles: any };


  constructor(
    private graphQLService: GraphQLService
  ) {
    this.options = { countries: [ ], centers: [ ], roles: [ ]};
  }

  // Form
  public fillOutUserFormSelectors(): Observable<{ countries: any, centers: any, roles: any }> {
    return this.graphQLService.query(`
      query {
        countries:allPais {
          value:paisId
          option:nombre
        }
        centers:allCentroCosto {
          value:centroCostoId
          option:nombre
        }
      }`, { }, true).pipe(
      map(({ data: { countries, centers }}: any) => ({ countries, centers, roles: [
        { value: UserRole.ADMINISTRATOR, option: 'Administrador' },
        { value: UserRole.COLLABORATOR, option: 'Colaborador' }
      ]})),
      tap(options => this.options = options)
    );
  }


  public doesUserExist(email: string): Observable<any> {
    return this.graphQLService.query(`
      query doesUserExist( $email: String! ) {
        doesUserExist:getUsuarioByEmail( email: $email ){
          email
        }
      }`, {
      email
    }).pipe(map(({ data: { doesUserExist }}: any) => !!doesUserExist?.email));
  }


  public getUser(email: string): Observable<any> {
    return this.graphQLService.query(`
      query doesUserExist( $email: String! ) {
        user:getUsuarioByEmail( email: $email ){
          id:usuarioId
          center:centroCosto {
            value:centroCostoId
            option:nombre
          }
          email:email
          status:estado
          position:cargo
          name:nombre
          country:pais {
            value:paisId
            option:nombre
          }
          role:rol
        }
      }`, {
      email: email.toLowerCase()
    }).pipe(map(({ data: { user }}: any) => user));
  }


  public createUser(newUser: Partial<User>): Observable<any> {
    return this.graphQLService.mutation(`
      mutation createUser( $newUser: UsuarioInput! ) {
        createUser:addUsuario( usuario: $newUser ) {
          email
        }
      }`, {
      newUser: this.translateUserProps({ ...newUser, status: UserStatus.ON })
    });
  }

  // Table
  public getUsers(): Observable<User[]> {
    return this.graphQLService.query(`
      query getUsers {
        users:allUsuario {
          id:usuarioId
          center:centroCosto {
            value:centroCostoId
            option:nombre
          }
          email:email
          status:estado
          position:cargo
          name:nombre
          country:pais {
            value:paisId
            option:nombre
          }
          role:rol
        }
      }`).pipe(map(({ data: { users }}: any) => users.map((
        { __typename, center: { option: center }, country: { option: country }, ...user }: any) => (
        { ...user, center, country }
      ))
    ));
  }


  public triggerUserStatus(id: number, updatedUser: User): Observable<any> {
    return this.editUser(id, {
      ...updatedUser,
      status: updatedUser.status === UserStatus.ON ? UserStatus.OFF : UserStatus.ON
    });
  }


  public editUser(id: number, updatedUser: User): Observable<any> {
    return this.graphQLService.mutation(`
      mutation updateUser( $id: Float!, $updatedUser: UsuarioInput! ) {
        updateUser:updateUsuario( usuarioId: $id, usuario: $updatedUser ) {
          id:usuarioId
          center:centroCosto {
            value:centroCostoId
            option:nombre
          }
          email:email
          status:estado
          position:cargo
          name:nombre
          country:pais {
            value:paisId
            option:nombre
          }
          role:rol
        }
      }`, {
        id, updatedUser: this.translateUserProps(updatedUser)
      }).pipe(map(({ data: {
        updateUser: { __typename, center: { option: center }, country: { option: country }, ...user }}}: any
      ) => ({ ...user, center, country })
    ));
  }


  private translateUserProps(user: Partial<User>): any {
    const country = this.options.countries.find(({ value, option }: any) => [ value, option ].includes(user.country)),
          center = this.options.centers.find(({ value, option }: any) => [ value, option ].includes(user.center)),
          role = this.options.roles.find(({ option }: any) => option.toUpperCase() === user.role);

    return {
      email: user.email,
      nombre: user.name,
      cargo: user.position,
      ...!!role?.value && { rol: role.value },
      ...!!center?.value && {
        centroCosto: {
          centroCostoId: center.value,
          nombre: center.option
        }
      },
      ...!!country?.value && {
        pais: {
          paisId: country.value,
          nombre: country.option
        }
      },
      ...!!user.status && { estado: user.status },
    }
  }
}
